<template>
  <section 
    id="blog-preview" 
    class="container py-24 sm:py-32"
    aria-labelledby="blog-preview-title"
  >
    <header class="text-center mb-12">
      <h2 
        id="blog-preview-title"
        class="text-3xl md:text-4xl font-bold mb-4"
      >
        <span class="bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">
          技术洞察与行业动态
        </span>
      </h2>
      <p class="text-lg text-muted-foreground max-w-2xl mx-auto">
        探索物联网、AI和工业自动化领域的最新技术趋势与实践经验
      </p>
    </header>

    <div 
      class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8"
      role="list"
      aria-label="精选技术文章"
    >
      <article
        v-for="article in featuredArticles"
        :key="article.id"
        class="group overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer"
        @click="navigateToBlog"
        role="listitem"
        itemscope
        itemtype="https://schema.org/BlogPosting"
      >
        <Card class="h-full flex flex-col">
          <div class="relative overflow-hidden">
            <div class="aspect-video overflow-hidden bg-muted">
              <img
                :src="article.image"
                :alt="`${article.title} - ${article.category}技术文章`"
                class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                loading="lazy"
                itemprop="image"
              />
            </div>
            <Badge class="absolute top-4 left-4">
              <span itemprop="articleSection">{{ article.category }}</span>
            </Badge>
          </div>

          <CardHeader class="flex-grow">
            <CardTitle 
              class="line-clamp-2 group-hover:text-primary transition-colors"
              itemprop="headline"
            >
              {{ article.title }}
            </CardTitle>
            <CardDescription class="line-clamp-2" itemprop="description">
              {{ article.description }}
            </CardDescription>
          </CardHeader>

          <CardContent>
            <div class="flex items-center justify-between text-sm text-muted-foreground">
              <time 
                class="flex items-center gap-1" 
                :datetime="article.date"
                itemprop="datePublished"
              >
                <Calendar class="w-4 h-4" aria-hidden="true" />
                <span>{{ formatDate(article.date) }}</span>
              </time>
              <div class="flex items-center gap-1">
                <Clock class="w-4 h-4" aria-hidden="true" />
                <span>{{ article.readTime }}</span>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <!-- SEO优化的隐藏元数据 -->
        <meta itemprop="author" :content="article.author" />
        <meta itemprop="dateModified" :content="article.date" />
      </article>
    </div>

    <div class="text-center">
      <Button
        size="lg"
        variant="outline"
        class="group"
        @click="navigateToBlog"
        aria-label="查看全部技术文章"
      >
        查看更多文章
        <ArrowRight class="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
      </Button>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Calendar, Clock, ArrowRight } from 'lucide-vue-next'

const router = useRouter()

interface Article {
  id: number
  title: string
  description: string
  category: string
  author: string
  date: string
  readTime: string
  image: string
}

// 精选文章（显示最新的3篇）
const featuredArticles = ref<Article[]>([
  {
    id: 1,
    title: 'AI驱动的智能能耗管理系统：从理论到实践',
    description: '深入探讨如何利用机器学习算法优化企业能源消耗，实现碳中和目标',
    category: '人工智能',
    author: '张工程师',
    date: '2025-10-10',
    readTime: '8分钟',
    image: '/hero-image-light.jpg',
  },
  {
    id: 2,
    title: '工业物联网在智慧工厂中的深度应用',
    description: '全面介绍工业物联网技术如何赋能传统制造业数字化转型',
    category: '物联网',
    author: '李经理',
    date: '2025-10-08',
    readTime: '10分钟',
    image: '/gamestation.png',
  },
  {
    id: 3,
    title: 'SCADA系统架构设计与安全最佳实践',
    description: '分享大型工业自动化控制系统的完整设计经验和安全防护策略',
    category: '工业自动化',
    author: '王主任',
    date: '2025-10-05',
    readTime: '12分钟',
    image: '/roboto.png',
  },
])

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr)
  const month = date.getMonth() + 1
  const day = date.getDate()
  return `${month}月${day}日`
}

const navigateToBlog = () => {
  router.push('/blog')
}
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>

